import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { resolveImg } from "@/lib/api";

// Searchable Product Master picker (SKU / name). Used on every itemization row so
// a piece is always a child of an existing design - never a free-text product.
export const ProductPicker = ({ products, value, onChange, disabled, testId = "product-picker" }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => products.find((p) => p.id === value), [products, value]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid={testId} type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
                className="w-full justify-between font-normal h-10 min-w-0">
          <span className="truncate text-left min-w-0">
            {selected ? <><span className="font-mono text-xs mr-2 text-muted-foreground">{selected.sku}</span>{selected.name}</> : "Select Product Master..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search SKU or name..." data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>No product master found.</CommandEmpty>
            <CommandGroup>
              {products.map((p) => (
                <CommandItem key={p.id} value={`${p.sku} ${p.name}`} onSelect={() => { onChange(p.id); setOpen(false); }} data-testid={`${testId}-option-${p.sku}`}>
                  <img src={resolveImg((p.images || [])[0])} alt="" className="h-7 w-7 rounded object-cover mr-2 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{p.sku} · {p.gold?.karat || "-"} · stock {p.stock ?? 0}</div>
                  </div>
                  {p.id === value && <Check className="h-4 w-4 text-[hsl(var(--primary))]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
